const MOBILE_QUERY = "(max-width: 767px), (hover: none) and (pointer: coarse)"
const EDITABLE =
  'textarea, input:not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([type="range"]):not([type="color"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="image"]):not([type="hidden"]), [contenteditable="true"], [contenteditable="plaintext-only"]'

/** Keyboard resize changes the visual viewport on iOS and modern Android, not 100vh. */
export function observeMobileViewport() {
  const mobile = window.matchMedia(MOBILE_QUERY)
  const viewport = window.visualViewport
  const style = document.documentElement.style
  let frame = 0
  let revealFocus = false
  let constrainedEditor: HTMLTextAreaElement | undefined
  let editorLimit = 0

  const releaseEditor = () => {
    constrainedEditor?.removeAttribute("data-viewport-constrained")
    constrainedEditor?.style.removeProperty("--app-editor-height")
    constrainedEditor = undefined
    editorLimit = 0
  }

  const clear = () => {
    releaseEditor()
    style.removeProperty("--app-viewport-height")
    style.removeProperty("--app-viewport-top")
  }

  const update = () => {
    frame = 0
    // A pinch gesture also shrinks visualViewport. Keep the existing layout while zooming;
    // reflowing it to that height would move the content the reader is trying to magnify.
    if (!mobile.matches || (viewport && Math.abs(viewport.scale - 1) > 0.05)) {
      if (!mobile.matches) clear()
      revealFocus = false
      return
    }

    const height = viewport?.height ?? window.innerHeight
    // Safari can retain its keyboard pan after restoring the full viewport height. Never
    // publish an offset that moves the visible area's bottom beyond the layout viewport.
    const layoutHeight = Math.max(
      document.documentElement.clientHeight,
      window.innerHeight
    )
    const top = Math.max(
      0,
      Math.min(viewport?.offsetTop ?? 0, Math.max(0, layoutHeight - height))
    )
    style.setProperty("--app-viewport-height", `${height}px`)
    style.setProperty("--app-viewport-top", `${top}px`)

    const focused = document.activeElement
    if (constrainedEditor && constrainedEditor !== focused) releaseEditor()
    if (
      revealFocus &&
      focused instanceof HTMLElement &&
      focused.matches(EDITABLE)
    ) {
      let rect = focused.getBoundingClientRect()
      const margin = 12
      let visibleTop = top
      let visibleBottom = top + height
      for (
        let parent = focused.parentElement;
        parent;
        parent = parent.parentElement
      ) {
        if (parent === document.scrollingElement) break
        if (!/auto|scroll|hidden|clip/.test(getComputedStyle(parent).overflowY))
          continue
        const bounds = parent.getBoundingClientRect()
        if (bounds.height > 0) {
          visibleTop = Math.max(visibleTop, bounds.top)
          visibleBottom = Math.min(visibleBottom, bounds.bottom)
        }
      }
      const available = Math.max(1, visibleBottom - visibleTop - margin * 2)
      if (focused instanceof HTMLTextAreaElement) {
        // A rubric can be taller than the transcript left above the document-pair composer.
        // Its caret must scroll inside a visible textarea, rather than into a clipped tail.
        if (constrainedEditor === focused && available > editorLimit) {
          releaseEditor()
          rect = focused.getBoundingClientRect()
        }
        if (rect.height > available) {
          constrainedEditor = focused
          editorLimit = available
          focused.style.setProperty("--app-editor-height", `${available}px`)
          focused.setAttribute("data-viewport-constrained", "true")
          rect = focused.getBoundingClientRect()
        }
      }
      const bottom = visibleBottom - margin
      // Tall editors scroll their own text. Keep their top reachable rather than oscillating
      // between trying to fit both edges of a field taller than the keyboard's remaining space.
      let delta =
        rect.height > visibleBottom - visibleTop - margin * 2
          ? rect.top - visibleTop - margin
          : rect.bottom > bottom
            ? rect.bottom - bottom
            : rect.top < visibleTop + margin
              ? rect.top - visibleTop - margin
              : 0

      // scrollIntoView uses the layout viewport and can consider a keyboard-covered field
      // already visible. Scroll the actual overflowing ancestor first, including modal forms.
      for (
        let parent = focused.parentElement;
        parent && Math.abs(delta) > 1;
        parent = parent.parentElement
      ) {
        if (parent === document.scrollingElement) break
        const overflow = getComputedStyle(parent).overflowY
        if (
          !/auto|scroll/.test(overflow) ||
          parent.scrollHeight <= parent.clientHeight
        )
          continue
        const previous = parent.scrollTop
        parent.scrollTop += delta
        delta -= parent.scrollTop - previous
      }
      if (Math.abs(delta) > 1)
        window.scrollBy({ top: delta, behavior: "instant" })
    }
    revealFocus = false
  }

  const schedule = (reveal: boolean) => {
    revealFocus ||= reveal
    if (!frame) frame = window.requestAnimationFrame(update)
  }
  const resize = () => schedule(true)
  // Panning changes the fixed chat's offset, but must not fight a reader scrolling a transcript.
  const pan = () => schedule(false)
  const focus = () => schedule(true)

  update()
  viewport?.addEventListener("resize", resize)
  viewport?.addEventListener("scroll", pan)
  window.addEventListener("resize", resize)
  mobile.addEventListener("change", resize)
  document.addEventListener("focusin", focus)
  document.addEventListener("focusout", pan)
  document.addEventListener("input", focus)

  return () => {
    window.cancelAnimationFrame(frame)
    viewport?.removeEventListener("resize", resize)
    viewport?.removeEventListener("scroll", pan)
    window.removeEventListener("resize", resize)
    mobile.removeEventListener("change", resize)
    document.removeEventListener("focusin", focus)
    document.removeEventListener("focusout", pan)
    document.removeEventListener("input", focus)
    clear()
  }
}
